/**
 * variableUtils — Step 18.1
 *
 * Pure utilities for runtime variable state management.
 * Used by usePreviewRuntime and testable in isolation.
 *
 * Runtime variable state is a plain Record<string, unknown> that lives
 * inside the Preview hook.  It is:
 *   - initialized from project.variables on every Preview mount
 *   - reset to configured defaults when Preview is unmounted and remounted
 *   - never written back to the canonical project document
 *   - never persisted to SQLite
 *
 * The canonical project.variables object is never mutated here.
 */

import type { Variable } from '../../types/project';

/** Ephemeral runtime variable state. Keys are variable names; values are
 *  the current runtime values (may differ from the configured defaultValue
 *  after response bindings have run). */
export type VariableRuntimeState = Record<string, unknown>;

/**
 * Recursively clones a JSON-compatible value so that the returned copy shares
 * no references with the original.
 *
 * Rules:
 *   - `null`              → `null`   (preserved exactly; must NOT become undefined)
 *   - primitive values    → returned as-is (they are immutable by value)
 *   - plain arrays        → new array whose elements are each recursively cloned
 *   - plain objects       → new object whose values are each recursively cloned
 *
 * This is intentionally narrower than a general deep-clone — it handles only
 * the JSON-compatible shapes that can appear in project.variables.defaultValue.
 */
function cloneJsonValue(value: unknown): unknown {
  if (value === null) return null;
  if (typeof value !== 'object') return value; // string, number, boolean, undefined
  if (Array.isArray(value)) {
    return value.map(cloneJsonValue);
  }
  // Plain object
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(value as Record<string, unknown>)) {
    result[key] = cloneJsonValue((value as Record<string, unknown>)[key]);
  }
  return result;
}

/**
 * Builds an initial VariableRuntimeState from a project's variable declarations.
 *
 * Initialization rules (per variable):
 *   - `defaultValue` property absent     → runtime value is `undefined`
 *   - `defaultValue: null`               → runtime value is `null`
 *   - any other configured value         → runtime value is a deep clone of that value
 *
 * Object and array defaults are deep-cloned so that runtime mutations never
 * affect the canonical project.variables configuration.
 *
 * Note: do NOT use `?? undefined` — that would silently convert explicit `null`
 * to `undefined`, losing the distinction between "no default" and "null default".
 *
 * The canonical `variables` object is never mutated.
 */
export function initializeVariableState(
  variables: Record<string, Variable>,
): VariableRuntimeState {
  const state: VariableRuntimeState = {};
  for (const [name, variable] of Object.entries(variables)) {
    // Use 'defaultValue' in variable to distinguish absence from null.
    state[name] = 'defaultValue' in variable ? cloneJsonValue(variable.defaultValue) : undefined;
  }
  return state;
}
